import {
  computeDeterministicPositionState,
  type PrimitiveExecution,
  type PrimitivePhase,
} from './deterministicPrimitives';

export type MetricExecution = {
  id?: string;
  timestamp: number;
  side: 'BUY' | 'SELL' | 'buy' | 'sell';
  qty: number;
  price: number;
  fee?: number;
};

export type MetricBar = {
  time: number;
  high: number;
  low: number;
};

type PhaseDirection = 'long' | 'short';

type PhaseDebugPoint = {
  time: number | null;
  price: number | null;
  pnl: number;
};

export type DeterministicPhaseMetrics = {
  id: string;
  direction: PhaseDirection;
  startTime: number;
  endTime: number | null;
  actualGross: number;
  actualNet: number;
  totalFees: number;
  mae: number;
  mfe: number;
  bestExitGross: number;
  bestExitNet: number;
  efficiencyGrossPct: number;
  efficiencyNetPct: number;
  excursionBarCount: number;
  excursionPointCount: number;
  debug: {
    bestFavorable: PhaseDebugPoint;
    worstAdverse: PhaseDebugPoint;
  };
};

export type DeterministicMetricsResult = {
  multiplier: number;
  clampEfficiency: boolean;
  orderedExecutionIds: string[];
  phases: DeterministicPhaseMetrics[];
};

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function efficiencyPct(actual: number, best: number, clampEfficiency: boolean): number {
  if (!Number.isFinite(best) || best === 0) return 0;
  const raw = (actual / best) * 100;
  if (!clampEfficiency) return raw;
  return Math.max(0, raw);
}

function isDevEnv(): boolean {
  try {
    return Boolean((import.meta as any)?.env?.DEV) || (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production');
  } catch {
    return typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : false;
  }
}

function toPrimitiveExecutions(executions: MetricExecution[]): PrimitiveExecution[] {
  return (executions || []).map((execution, index) => ({
    id: execution.id,
    timestamp: Number(execution.timestamp),
    side: execution.side,
    qty: Number(execution.qty),
    price: Number(execution.price),
    fee: Number(execution.fee || 0),
    originalIndex: index,
  }));
}

function computePhaseExcursions(params: {
  phase: PrimitivePhase;
  bars: MetricBar[];
  multiplier: number;
}): {
  mfe: number;
  mae: number;
  excursionBarCount: number;
  excursionPointCount: number;
  bestFavorable: PhaseDebugPoint;
  worstAdverse: PhaseDebugPoint;
} {
  const { phase, bars, multiplier } = params;

  const scopedBars = (bars || []).filter((bar) => {
    const t = Number(bar.time);
    if (!Number.isFinite(t)) return false;
    if (t < phase.startTime) return false;
    if (phase.endTime != null && t > phase.endTime) return false;
    return true;
  });

  const ops = [...(phase.ops || [])].sort((a, b) => {
    const dt = a.time - b.time;
    if (dt !== 0) return dt;
    if (a.seq !== b.seq) return a.seq - b.seq;
    return a.opOrder - b.opOrder;
  });

  const activeLots: Array<{ qty: number; price: number }> = [];
  let opIndex = 0;
  let realizedGross = 0;
  let mfe = 0;
  let mae = 0;
  let bestFavorable: PhaseDebugPoint = { time: null, price: null, pnl: 0 };
  let worstAdverse: PhaseDebugPoint = { time: null, price: null, pnl: 0 };
  let excursionBarCount = 0;
  let excursionPointCount = 0;

  const markOpenPosition = (time: number, price: number) => {
    if (!Number.isFinite(time) || !Number.isFinite(price)) return;
    const openGross = activeLots.reduce((sum, lot) => {
      const pnl = phase.direction === 'long'
        ? (price - lot.price) * lot.qty * multiplier
        : (lot.price - price) * lot.qty * multiplier;
      return sum + pnl;
    }, 0);
    const pnl = realizedGross + openGross;
    excursionPointCount += 1;

    if (pnl > mfe) {
      mfe = pnl;
      bestFavorable = { time, price, pnl };
    }

    if (pnl < mae) {
      mae = pnl;
      worstAdverse = { time, price, pnl };
    }
  };

  const applyOp = (op: (typeof ops)[number]) => {
    if (op.kind === 'open') {
      activeLots.push({ qty: op.qty, price: op.price });
      markOpenPosition(op.time, op.price);
      return;
    }

    let remaining = op.qty;
    while (remaining > 0 && activeLots.length > 0) {
      const lot = activeLots[0];
      const matched = Math.min(remaining, lot.qty);
      realizedGross += phase.direction === 'long'
        ? (op.price - lot.price) * matched * multiplier
        : (lot.price - op.price) * matched * multiplier;
      lot.qty -= matched;
      remaining -= matched;
      if (lot.qty <= 0) activeLots.shift();
    }
    markOpenPosition(op.time, op.price);
  };

  for (const bar of scopedBars) {
    const barTime = Number(bar.time);

    while (opIndex < ops.length && ops[opIndex].time <= barTime) {
      applyOp(ops[opIndex]);
      opIndex += 1;
    }

    const openQty = activeLots.reduce((sum, lot) => sum + lot.qty, 0);
    if (openQty <= 0) continue;

    const high = Number(bar.high);
    const low = Number(bar.low);
    if (!Number.isFinite(high) || !Number.isFinite(low)) continue;
    excursionBarCount += 1;

    let favorable = realizedGross;
    let adverse = realizedGross;

    for (const lot of activeLots) {
      if (phase.direction === 'long') {
        favorable += (high - lot.price) * lot.qty * multiplier;
        adverse += (low - lot.price) * lot.qty * multiplier;
      } else {
        favorable += (lot.price - low) * lot.qty * multiplier;
        adverse += (lot.price - high) * lot.qty * multiplier;
      }
    }

    if (favorable > mfe) {
      mfe = favorable;
      bestFavorable = {
        time: barTime,
        price: phase.direction === 'long' ? high : low,
        pnl: favorable,
      };
    }

    if (adverse < mae) {
      mae = adverse;
      worstAdverse = {
        time: barTime,
        price: phase.direction === 'long' ? low : high,
        pnl: adverse,
      };
    }
  }

  while (opIndex < ops.length) {
    applyOp(ops[opIndex]);
    opIndex += 1;
  }

  return {
    mfe,
    mae,
    excursionBarCount,
    excursionPointCount,
    bestFavorable,
    worstAdverse,
  };
}

export function calculateDeterministicTradeMetrics(input: {
  executions: MetricExecution[];
  bars: MetricBar[];
  multiplier?: number;
  clampEfficiency?: boolean;
  debug?: boolean;
}): DeterministicMetricsResult {
  const multiplier = Number.isFinite(Number(input.multiplier)) ? Number(input.multiplier) : 1;
  const clampEfficiencyMode = Boolean(input.clampEfficiency);

  const state = computeDeterministicPositionState({
    executions: toPrimitiveExecutions(input.executions),
    multiplier,
    markPrice: null,
  });

  const phaseMetrics = state.phases
    .map((phase) => {
      const excursions = computePhaseExcursions({ phase, bars: input.bars || [], multiplier });
      const bestExitGross = excursions.mfe;
      const actualGross = phase.actualGross;
      const totalFees = phase.totalFees;
      const actualNet = actualGross - totalFees;
      const bestExitNet = bestExitGross - totalFees;

      const metrics: DeterministicPhaseMetrics = {
        id: phase.id,
        direction: phase.direction,
        startTime: phase.startTime,
        endTime: phase.endTime,
        actualGross: round2(actualGross),
        actualNet: round2(actualNet),
        totalFees: round2(totalFees),
        mae: round2(excursions.mae),
        mfe: round2(excursions.mfe),
        bestExitGross: round2(bestExitGross),
        bestExitNet: round2(bestExitNet),
        efficiencyGrossPct: round2(efficiencyPct(actualGross, bestExitGross, clampEfficiencyMode)),
        efficiencyNetPct: round2(efficiencyPct(actualNet, bestExitNet, clampEfficiencyMode)),
        excursionBarCount: excursions.excursionBarCount,
        excursionPointCount: excursions.excursionPointCount,
        debug: {
          bestFavorable: { ...excursions.bestFavorable, pnl: round2(excursions.bestFavorable.pnl) },
          worstAdverse: { ...excursions.worstAdverse, pnl: round2(excursions.worstAdverse.pnl) },
        },
      };

      if (input.debug && isDevEnv()) {
        console.debug(
          `[deterministic-metrics] ${metrics.id} (${metrics.direction}) best@${metrics.debug.bestFavorable.time} ` +
          `price=${metrics.debug.bestFavorable.price} pnl=${metrics.debug.bestFavorable.pnl}; ` +
          `worst@${metrics.debug.worstAdverse.time} price=${metrics.debug.worstAdverse.price} pnl=${metrics.debug.worstAdverse.pnl}`
        );
      }

      return metrics;
    })
    .sort((a, b) => a.startTime - b.startTime);

  return {
    multiplier,
    clampEfficiency: clampEfficiencyMode,
    orderedExecutionIds: state.orderedExecutionIds,
    phases: phaseMetrics,
  };
}
